import { useState } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { appRoutes } from '../routes/routes';
import { missingRequirements } from '../../platform/support/browser-support';
import { AppErrorBoundary } from './ErrorBoundary';
import { AppServicesProvider } from './AppServicesProvider';
import { UnsupportedBrowserScreen } from './UnsupportedBrowserScreen';
import './app.css';

const router = createBrowserRouter(appRoutes);

/**
 * The whole shell sits inside one error boundary, and every route also carries its own error
 * element. A child should never be looking at a white page (architecture §22).
 *
 * The support check runs **before** the router, and on every render rather than once at module
 * load, so a test can present a browser without breaking module caching. It is a *check*, not a
 * gate on a browser's name: `missingRequirements` reads capabilities only (DEP-03).
 */
export function App() {
  /**
   * Asked **once per mount**, not once per render.
   *
   * The canvas probe creates a rendering context, and a browser keeps only so many alive before it
   * starts dropping the oldest — so re-probing on every render is a way to break the very drawing
   * surface it is checking for. What a browser can do does not change during a sitting; a lazy
   * initialiser is both the cheap answer and the honest one.
   */
  const [missing] = useState(missingRequirements);
  if (missing.length > 0) {
    return (
      <AppErrorBoundary>
        <UnsupportedBrowserScreen missing={missing} />
      </AppErrorBoundary>
    );
  }

  return (
    <AppErrorBoundary>
      <AppServicesProvider>
        <RouterProvider router={router} />
      </AppServicesProvider>
    </AppErrorBoundary>
  );
}
